<template>
  <teleport to="body">
    <transition
      name="sm-modal__animation"
      @before-enter="handleBeforeEnter"
      @after-enter="handleAfterEnter"
      @before-leave="handleBeforeLeave"
      @after-leave="handleAfterLeave"
    >
      <div
        v-if="isVisible"
        class="sm-modal"
        :class="classList"
        @click="handleCloseByBackdrop"
      >
        <transition name="sm-modal__animation-window">
          <div
            v-show="showModalWindow"
            class="sm-modal__window"
            :style="{ width, height, maxWidth, maxHeight }"
            @click.stop
          >
            <header
              v-if="showHeader"
              class="sm-modal__header"
            >
              <slot name="header">
                <div class="sm-modal__header-wrap">
                  <slot name="header-content">
                    <p
                      v-if="title"
                      class="sm-modal__title"
                    >
                      {{ title }}
                    </p>
                  </slot>
                  <sm-icon
                    v-if="showCloseIcon"
                    name="Cancel"
                    :size="24"
                    class="sm-modal__close"
                    @click="handleClose"
                  />
                </div>
              </slot>
            </header>
            
            <section class="sm-modal__body">
              <slot name="default"></slot>
            </section>
            
            <footer
              v-if="showFooter"
              class="sm-modal__footer"
            >
              <div class="sm-modal__footer-wrap">
                <slot name="footer">
                  <template v-if="isFooterButtons">
                    <sm-button
                      v-if="isLeftButton"
                      :type="leftButtonType"
                      :disabled="isDisabledLeftButton"
                      :loading="isLoadingLeftButton"
                      @click="handleClickLeftButton"
                    >
                      {{ leftButtonText }}
                    </sm-button>
                    <sm-button
                      v-if="isRightButton"
                      :type="rightButtonType"
                      :disabled="isDisabledRightButton"
                      :loading="isLoadingRightButton"
                      @click="handleClickRightButton"
                    >
                      {{ rightButtonText }}
                    </sm-button>
                  </template>
                </slot>
              </div>
            </footer>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts" setup>
// Modules
import { useSlots, computed, ref, toRefs } from 'vue';

// Components
import SmIcon from '@/components/common/SmIcon/SmIcon.vue';
import SmButton from '@/components/common/buttons/SmButton.vue';

// Types
import { IModalProps } from '@/types/components/modal';
import { Class } from '@/types/common';

// Composables
const { header, footer }: any = useSlots();

// Props
const props = withDefaults(
  defineProps<IModalProps>(),
  {
    isVisible: false,
    title: '',
    width: '402px',
    height: 'auto',
    maxWidth: 'initial',
    maxHeight: '100vh',
    isCancebleBackdrop: true,
    showCloseIcon: true,
    isLeftButton: true,
    isRightButton: true,
    leftButtonText: 'Отмена',
    rightButtonText: 'Принять',
    leftButtonType: 'neutral',
    rightButtonType: 'primary',
    isDisabledLeftButton: false,
    isDisabledRightButton: false,
    isLoadingLeftButton: false,
    isLoadingRightButton: false,
  }
);

const {
  isVisible,
  title,
  width,
  height,
  maxWidth,
  maxHeight,
  isCancebleBackdrop,
  showCloseIcon,
  isLeftButton,
  isRightButton,
  leftButtonText,
  rightButtonText,
  leftButtonType,
  rightButtonType,
  isDisabledLeftButton,
  isDisabledRightButton,
  isLoadingLeftButton,
  isLoadingRightButton,
} = toRefs(props);

// Emits
const emits = defineEmits<{
  (e: 'open' | 'openned' | 'close' | 'closed' | 'leftButton' | 'rightButton'): void;
}>();

// Data
const showModalWindow = ref<boolean>(false);

// Computed
const classList = computed((): Class => ([
  { 
    'sm-modal--closable-backdrop': isCancebleBackdrop.value,
    'sm-modal--opened': isVisible.value
  }
]));

const showHeader = computed((): boolean => (
  header || title
));

const showFooter = computed((): boolean => (
  footer || isFooterButtons.value
));

const isFooterButtons = computed((): boolean => (
  isLeftButton.value || isRightButton.value
));

// Methods
const handleCloseByBackdrop = (): void => {
  if (!isCancebleBackdrop.value) return;
  
  closeModal();
};

const handleClose = (): void => {
  closeModal();
};

// Transition methods
const handleBeforeEnter = (): void => {
  emits('open');
};
const handleAfterEnter = (): void => {
  showModalWindow.value = true;
  emits('openned');
};
const handleBeforeLeave = (): void => {
  showModalWindow.value = false;
  emits('close');
};
const handleAfterLeave = (): void => {
  emits('closed');
};

const closeModal = (): void => {
  emits('close');
};

const handleClickLeftButton = (): void => {
  emits('leftButton');
};

const handleClickRightButton = (): void => {
  emits('rightButton');
};

</script>

<style lang="scss">
.sm-modal {
  display: flex;
  justify-content: center;
  align-items: center;
  
  width: 100%;
  height: 100%;
  overflow-y: auto;
  
  position: fixed;
  top: 0;
  left: 0;
  z-index: 11000;
  
  background: linear-gradient(0deg, rgba(69, 88, 108, 0.33), rgba(69, 88, 108, 0.33));
}

html:has(.sm-modal--opened) {
  overflow: hidden;

  // TODO: добавить отступ на размер скролла
}

.sm-modal--closable-backdrop {
  cursor: pointer;
}

.sm-modal__window {  
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  padding-bottom: var(--padding);
  
  background-color: var(--Background);
  border-radius: var(--border-radius);

  cursor: default;
}

.sm-modal__header {
  padding: var(--padding) var(--padding) 0;
}

.sm-modal__header-wrap {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: nowrap;
}

.sm-modal__title {
  flex-basis: 100%;
  flex-grow: 1;
  
  @include h3;
  color: var(--Title);
  
  margin: 0;
}

.sm-modal__close {
  flex-shrink: 0;
  
  color: var(--Icon);
  
  cursor: pointer;
  
  @include transition(('color'));
  
  &:hover,
  &:active {
    color: var(--IconHoverPress);
  }
}

.sm-modal__body {
  flex-grow: 1;
  padding-inline: var(--padding);
  overflow: auto;
}

.sm-modal__footer {
  padding: 0 var(--padding);
}

.sm-modal__footer-wrap {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--gap-s);
}

// For root
.sm-modal__animation-enter-from,
.sm-modal__animation-leave-to {
  transition: opacity .15s;
  opacity: 0;
}

.sm-modal__animation-enter-active,
.sm-modal__animation-leader-active {
  transition: opacity .15s;
}

// For window
.sm-modal__animation-window-enter-from {
  transition: opacity .15s, transform .15s;
  opacity: 0;
  transform: translateY(-200%);
}

.sm-modal__animation-window-leave-active {
  transition: transform .15s;
  transform: translateY(200%);
}

.sm-modal__animation-window-enter-active,
.sm-modal__animation-window-leader-active {
  transition: opacity .15s, transform .15s;
}
</style>